import supabase from '../supabase/supabase';

const requireOrganizationId = (organizationId) => {
  if (!organizationId) {
    throw new Error('Organization ID is required.');
  }
};

const requireInvoiceId = (invoiceId) => {
  if (!invoiceId) {
    throw new Error('Invoice ID is required.');
  }
};

const requireCustomerId = (customerId) => {
  if (!customerId) {
    throw new Error('Customer ID is required.');
  }
};

const normalizeCurrency = (currency) =>
  String(currency || 'USD')
    .trim()
    .toUpperCase();

const cleanText = (value) =>
  String(value ?? '').trim();

const toMinorUnits = (value) => {
  const number = Number(value || 0);

  if (!Number.isFinite(number)) {
    return 0;
  }

  return Math.round(number * 100);
};

const fromMinorUnits = (value) => {
  const number = Number(value || 0);

  if (!Number.isFinite(number)) {
    return 0;
  }

  return number / 100;
};

const normalizeQuantity = (value) => {
  const quantity = Number(value || 0);

  if (!Number.isFinite(quantity)) {
    return 0;
  }

  return quantity;
};

const calculateInvoiceAmounts = ({
  items = [],
  taxAmount = 0,
  discountAmount = 0
}) => {
  const normalizedItems = items.map((item) => {
    const quantity = normalizeQuantity(
      item.quantity ?? item.qty ?? 1
    );

    const unitAmount = toMinorUnits(
      item.unitAmount ??
        item.unit_amount ??
        item.price ??
        0
    );

    const amount = Math.round(
      quantity * unitAmount
    );

    return {
      ...item,
      quantity,
      unitAmount,
      amount
    };
  });

  const subtotal = normalizedItems.reduce(
    (sum, item) => sum + item.amount,
    0
  );

  const tax = toMinorUnits(taxAmount);
  const discount =
    toMinorUnits(discountAmount);

  const total = Math.max(
    0,
    subtotal + tax - discount
  );

  return {
    items: normalizedItems,
    subtotal,
    tax,
    discount,
    total
  };
};

class InvoiceService {
  /*
   * ---------------------------------------------------------
   * MONEY HELPERS
   * ---------------------------------------------------------
   */

  toMinorUnits(value) {
    return toMinorUnits(value);
  }

  fromMinorUnits(value) {
    return fromMinorUnits(value);
  }

  /*
   * ---------------------------------------------------------
   * CUSTOMERS
   * ---------------------------------------------------------
   */

  async getCustomers(organizationId) {
    requireOrganizationId(
      organizationId
    );

    const { data, error } =
      await supabase
        .schema('accounting')
        .from('customers')
        .select(`
          id,
          organization_id,
          name,
          email,
          phone,
          company_name,
          address_line1,
          address_line2,
          city,
          state,
          postal_code,
          country,
          currency,
          stripe_customer_id,
          is_active,
          created_at,
          updated_at
        `)
        .eq(
          'organization_id',
          organizationId
        )
        .eq('is_active', true)
        .order('name', {
          ascending: true
        });

    if (error) {
      console.error(
        '[Invoices] Failed to load customers:',
        error
      );

      throw new Error(
        error.message ||
          'Unable to load customers.'
      );
    }

    return data || [];
  }

  async getCustomerById(
    organizationId,
    customerId
  ) {
    requireOrganizationId(
      organizationId
    );

    requireCustomerId(
      customerId
    );

    const { data, error } =
      await supabase
        .schema('accounting')
        .from('customers')
        .select('*')
        .eq(
          'organization_id',
          organizationId
        )
        .eq('id', customerId)
        .single();

    if (error) {
      console.error(
        '[Invoices] Failed to load customer:',
        error
      );

      throw new Error(
        error.message ||
          'Unable to load customer.'
      );
    }

    return data;
  }

  async createCustomer(
    organizationId,
    customerData
  ) {
    requireOrganizationId(
      organizationId
    );

    const name = cleanText(
      customerData?.name
    );

    const email = cleanText(
      customerData?.email
    );

    if (!name) {
      throw new Error(
        'Customer name is required.'
      );
    }

    if (!email) {
      throw new Error(
        'Customer email is required.'
      );
    }

    const payload = {
      organization_id:
        organizationId,

      name,

      email,

      phone:
        cleanText(
          customerData?.phone
        ),

      company_name:
        cleanText(
          customerData?.company_name ??
            customerData?.companyName
        ),

      address_line1:
        cleanText(
          customerData?.address_line1 ??
            customerData?.addressLine1
        ),

      address_line2:
        cleanText(
          customerData?.address_line2 ??
            customerData?.addressLine2
        ),

      city:
        cleanText(
          customerData?.city
        ),

      state:
        cleanText(
          customerData?.state
        ),

      postal_code:
        cleanText(
          customerData?.postal_code ??
            customerData?.postalCode
        ),

      country:
        cleanText(
          customerData?.country ||
            'US'
        ).toUpperCase(),

      currency:
        normalizeCurrency(
          customerData?.currency
        ),

      is_active: true
    };

    const { data, error } =
      await supabase
        .schema('accounting')
        .from('customers')
        .insert(payload)
        .select()
        .single();

    if (error) {
      console.error(
        '[Invoices] Failed to create customer:',
        error
      );

      throw new Error(
        error.message ||
          'Unable to create customer.'
      );
    }

    return data;
  }

  /*
   * ---------------------------------------------------------
   * INVOICES
   * ---------------------------------------------------------
   */

  async getInvoices(organizationId) {
    requireOrganizationId(
      organizationId
    );

    const { data, error } =
      await supabase
        .schema('accounting')
        .from('invoices')
        .select(`
          *,
          customer:customers (
            id,
            name,
            email,
            phone,
            company_name,
            currency,
            stripe_customer_id
          ),
          items:invoice_items (
            id,
            invoice_id,
            organization_id,
            account_id,
            description,
            quantity,
            unit_amount,
            amount,
            currency,
            stripe_invoice_item_id,
            created_at,
            updated_at
          )
        `)
        .eq(
          'organization_id',
          organizationId
        )
        .order('created_at', {
          ascending: false
        });

    if (error) {
      console.error(
        '[Invoices] Failed to load invoices:',
        error
      );

      throw new Error(
        error.message ||
          'Unable to load invoices.'
      );
    }

    return data || [];
  }

  async getInvoiceById(
    organizationId,
    invoiceId
  ) {
    requireOrganizationId(
      organizationId
    );

    requireInvoiceId(
      invoiceId
    );

    const { data, error } =
      await supabase
        .schema('accounting')
        .from('invoices')
        .select(`
          *,
          customer:customers (
            *
          ),
          items:invoice_items (
            *
          )
        `)
        .eq(
          'organization_id',
          organizationId
        )
        .eq('id', invoiceId)
        .single();

    if (error) {
      console.error(
        '[Invoices] Failed to load invoice:',
        error
      );

      throw new Error(
        error.message ||
          'Unable to load invoice.'
      );
    }

    return data;
  }

  /*
   * ---------------------------------------------------------
   * GENERATE NEXT INVOICE NUMBER
   * ---------------------------------------------------------
   */

  async getNextInvoiceNumber(
    organizationId
  ) {
    requireOrganizationId(
      organizationId
    );

    const year =
      new Date().getFullYear();

    const prefix =
      `INV-${year}-`;

    const { data, error } =
      await supabase
        .schema('accounting')
        .from('invoices')
        .select('invoice_number')
        .eq(
          'organization_id',
          organizationId
        )
        .like(
          'invoice_number',
          `${prefix}%`
        )
        .order(
          'invoice_number',
          {
            ascending: false
          }
        )
        .limit(1);

    if (error) {
      console.error(
        '[Invoices] Failed to generate invoice number:',
        error
      );

      throw new Error(
        'Unable to generate an invoice number.'
      );
    }

    let nextNumber = 1;

    if (
      data &&
      data.length > 0
    ) {
      const current =
        String(
          data[0]
            .invoice_number ||
            ''
        );

      const numericPart =
        Number(
          current.replace(
            prefix,
            ''
          )
        );

      if (
        Number.isFinite(
          numericPart
        )
      ) {
        nextNumber =
          numericPart + 1;
      }
    }

    return (
      prefix +
      String(nextNumber).padStart(
        4,
        '0'
      )
    );
  }

  /*
   * ---------------------------------------------------------
   * CREATE DRAFT INVOICE
   * ---------------------------------------------------------
   */

  async createInvoice(
    organizationId,
    invoiceData,
    items
  ) {
    requireOrganizationId(
      organizationId
    );

    requireCustomerId(
      invoiceData?.customer_id ??
        invoiceData?.customerId
    );

    if (
      !Array.isArray(items) ||
      items.length === 0
    ) {
      throw new Error(
        'Add at least one invoice item.'
      );
    }

    const customerId =
      invoiceData.customer_id ??
      invoiceData.customerId;

    const currency =
      normalizeCurrency(
        invoiceData.currency
      );

    const calculated =
      calculateInvoiceAmounts({
        items,

        taxAmount:
          invoiceData.taxAmount ??
          invoiceData.tax_amount ??
          0,

        discountAmount:
          invoiceData.discountAmount ??
          invoiceData.discount_amount ??
          0
      });

    if (
      calculated.subtotal <= 0
    ) {
      throw new Error(
        'Invoice total must be greater than zero.'
      );
    }

    let invoiceNumber =
      cleanText(
        invoiceData.invoice_number ??
          invoiceData.invoiceNumber
      );

    if (!invoiceNumber) {
      invoiceNumber =
        await this.getNextInvoiceNumber(
          organizationId
        );
    }

    const {
      data: { user },
      error: userError
    } =
      await supabase.auth.getUser();

    if (userError) {
      throw userError;
    }

    if (!user) {
      throw new Error(
        'You must be signed in to create an invoice.'
      );
    }

    const invoicePayload = {
      organization_id:
        organizationId,

      customer_id:
        customerId,

      invoice_number:
        invoiceNumber,

      status: 'draft',

      currency,

      subtotal:
        calculated.subtotal,

      tax_amount:
        calculated.tax,

      discount_amount:
        calculated.discount,

      total_amount:
        calculated.total,

      amount_paid: 0,

      amount_due:
        calculated.total,

      issue_date:
        invoiceData.issue_date ??
        invoiceData.issueDate ??
        new Date()
          .toISOString()
          .slice(0, 10),

      due_date:
        invoiceData.due_date ??
        invoiceData.dueDate ??
        null,

      collection_method:
        invoiceData.collection_method ??
        invoiceData.collectionMethod ??
        'send_invoice',

      days_until_due:
        invoiceData.days_until_due ??
        invoiceData.daysUntilDue ??
        null,

      payment_method_type:
        invoiceData.payment_method_type ??
        invoiceData.paymentMethodType ??
        null,

      memo:
        cleanText(
          invoiceData.memo
        ),

      footer:
        cleanText(
          invoiceData.footer
        ),

      created_by:
        user.id
    };

    /*
     * Create invoice first.
     */
    const {
      data: invoice,
      error: invoiceError
    } =
      await supabase
        .schema('accounting')
        .from('invoices')
        .insert(
          invoicePayload
        )
        .select()
        .single();

    if (invoiceError) {
      console.error(
        '[Invoices] Failed to create invoice:',
        invoiceError
      );

      throw new Error(
        invoiceError.message ||
          'Unable to create invoice.'
      );
    }

    /*
     * Create invoice line items.
     */
    const itemPayloads =
      calculated.items.map(
        (item) => ({
          invoice_id:
            invoice.id,

          organization_id:
            organizationId,

          account_id:
            item.account_id ??
            item.accountId ??
            null,

          description:
            cleanText(
              item.description
            ),

          quantity:
            item.quantity,

          unit_amount:
            item.unitAmount,

          amount:
            item.amount,

          currency
        })
      );

    const {
      error: itemsError
    } =
      await supabase
        .schema('accounting')
        .from('invoice_items')
        .insert(
          itemPayloads
        );

    if (itemsError) {
      console.error(
        '[Invoices] Failed to create invoice items:',
        itemsError
      );

      /*
       * Prevent leaving an empty invoice behind
       * when the item insert fails.
       */
      const {
        error: cleanupError
      } =
        await supabase
          .schema('accounting')
          .from('invoices')
          .delete()
          .eq(
            'id',
            invoice.id
          )
          .eq(
            'organization_id',
            organizationId
          );

      if (cleanupError) {
        console.error(
          '[Invoices] Failed to clean up incomplete invoice:',
          cleanupError
        );
      }

      throw new Error(
        itemsError.message ||
          'Invoice could not save its line items.'
      );
    }

    return this.getInvoiceById(
      organizationId,
      invoice.id
    );
  }

  /*
   * ---------------------------------------------------------
   * UPDATE DRAFT INVOICE
   * ---------------------------------------------------------
   */

  async updateInvoice(
    organizationId,
    invoiceId,
    invoiceData,
    items
  ) {
    requireOrganizationId(
      organizationId
    );

    requireInvoiceId(
      invoiceId
    );

    if (
      !Array.isArray(items) ||
      items.length === 0
    ) {
      throw new Error(
        'Add at least one invoice item.'
      );
    }

    const existing =
      await this.getInvoiceById(
        organizationId,
        invoiceId
      );

    if (
      existing.status !== 'draft'
    ) {
      throw new Error(
        'Only draft invoices can be edited.'
      );
    }

    const customerId =
      invoiceData.customer_id ??
      invoiceData.customerId ??
      existing.customer_id;

    requireCustomerId(
      customerId
    );

    const currency =
      normalizeCurrency(
        invoiceData.currency ??
          existing.currency
      );

    const calculated =
      calculateInvoiceAmounts({
        items,

        taxAmount:
          invoiceData.taxAmount ??
          invoiceData.tax_amount ??
          0,

        discountAmount:
          invoiceData.discountAmount ??
          invoiceData.discount_amount ??
          0
      });

    if (
      calculated.subtotal <= 0
    ) {
      throw new Error(
        'Invoice total must be greater than zero.'
      );
    }

    const invoicePayload = {
      customer_id:
        customerId,

      invoice_number:
        cleanText(
          invoiceData.invoice_number ??
            invoiceData.invoiceNumber ??
            existing.invoice_number
        ),

      currency,

      subtotal:
        calculated.subtotal,

      tax_amount:
        calculated.tax,

      discount_amount:
        calculated.discount,

      total_amount:
        calculated.total,

      amount_due:
        calculated.total,

      issue_date:
        invoiceData.issue_date ??
        invoiceData.issueDate ??
        existing.issue_date,

      due_date:
        invoiceData.due_date ??
        invoiceData.dueDate ??
        null,

      collection_method:
        invoiceData.collection_method ??
        invoiceData.collectionMethod ??
        existing.collection_method ??
        'send_invoice',

      days_until_due:
        invoiceData.days_until_due ??
        invoiceData.daysUntilDue ??
        null,

      payment_method_type:
        invoiceData.payment_method_type ??
        invoiceData.paymentMethodType ??
        null,

      memo:
        cleanText(
          invoiceData.memo
        ),

      footer:
        cleanText(
          invoiceData.footer
        ),

      updated_at:
        new Date().toISOString()
    };

    const {
      error: invoiceError
    } =
      await supabase
        .schema('accounting')
        .from('invoices')
        .update(
          invoicePayload
        )
        .eq(
          'id',
          invoiceId
        )
        .eq(
          'organization_id',
          organizationId
        );

    if (invoiceError) {
      console.error(
        '[Invoices] Failed to update invoice:',
        invoiceError
      );

      throw new Error(
        invoiceError.message ||
          'Unable to update invoice.'
      );
    }

    /*
     * Replace draft line items.
     */
    const {
      error: deleteItemsError
    } =
      await supabase
        .schema('accounting')
        .from('invoice_items')
        .delete()
        .eq(
          'invoice_id',
          invoiceId
        )
        .eq(
          'organization_id',
          organizationId
        );

    if (deleteItemsError) {
      throw new Error(
        deleteItemsError.message ||
          'Unable to update invoice items.'
      );
    }

    const itemPayloads =
      calculated.items.map(
        (item) => ({
          invoice_id:
            invoiceId,

          organization_id:
            organizationId,

          account_id:
            item.account_id ??
            item.accountId ??
            null,

          description:
            cleanText(
              item.description
            ),

          quantity:
            item.quantity,

          unit_amount:
            item.unitAmount,

          amount:
            item.amount,

          currency
        })
      );

    const {
      error: insertItemsError
    } =
      await supabase
        .schema('accounting')
        .from('invoice_items')
        .insert(
          itemPayloads
        );

    if (insertItemsError) {
      console.error(
        '[Invoices] Failed to replace invoice items:',
        insertItemsError
      );

      throw new Error(
        insertItemsError.message ||
          'Unable to update invoice items.'
      );
    }

    return this.getInvoiceById(
      organizationId,
      invoiceId
    );
  }

    /*
   * ---------------------------------------------------------
   * SEND INVOICE THROUGH STRIPE CONNECT
   * ---------------------------------------------------------
   */

  async sendInvoice(
    organizationId,
    invoiceId
  ) {
    requireOrganizationId(
      organizationId
    );

    requireInvoiceId(
      invoiceId
    );

    /*
     * Get the current session so the Edge Function
     * receives the user's JWT explicitly.
     */
    const {
      data: {
        session
      },
      error: sessionError
    } =
      await supabase.auth
        .getSession();

    if (sessionError) {
      console.error(
        '[Invoices] Failed to get session:',
        sessionError
      );

      throw new Error(
        'Unable to verify your session.'
      );
    }

    if (
      !session?.access_token
    ) {
      throw new Error(
        'You must be signed in to send an invoice.'
      );
    }

    /*
     * Call the secure Stripe Connect Edge Function.
     *
     * We only send:
     * - client organization ID
     * - local invoice ID
     *
     * The backend determines the managing firm
     * and Stripe connected account itself.
     */
    const {
      data,
      error
    } =
      await supabase.functions
        .invoke(
          'stripe-send-invoice',
          {
            body: {
              organizationId,
              invoiceId
            },

            headers: {
              Authorization:
                `Bearer ${session.access_token}`
            }
          }
        );

    /*
     * Supabase returns non-2xx Edge Function
     * responses as an error.
     *
     * Try to extract the actual message returned
     * by our Edge Function instead of showing only
     * "Edge Function returned a non-2xx status code".
     */
    if (error) {
      console.error(
        '[Invoices] Stripe send function failed:',
        error
      );

      let message =
        error.message ||
        'Unable to send invoice.';

      try {
        if (
          error.context &&
          typeof error.context.json ===
            'function'
        ) {
          const errorBody =
            await error.context.json();

          if (
            errorBody?.message
          ) {
            message =
              errorBody.message;
          }
        }
      } catch (
        parseError
      ) {
        console.warn(
          '[Invoices] Unable to parse Edge Function error response:',
          parseError
        );
      }

      throw new Error(
        message
      );
    }

    if (!data) {
      throw new Error(
        'Stripe did not return an invoice response.'
      );
    }

    if (
      data.success !== true
    ) {
      throw new Error(
        data.message ||
          'Unable to send invoice.'
      );
    }

    console.log(
      '[Invoices] Invoice sent successfully:',
      {
        organizationId,
        invoiceId,
        stripeInvoiceId:
          data.invoice
            ?.stripeInvoiceId,
        status:
          data.invoice
            ?.status
      }
    );

    /*
     * Reload from our own database instead of
     * relying entirely on Stripe's response.
     *
     * This gives the UI the same invoice shape
     * returned by getInvoices/getInvoiceById,
     * including customer + invoice items.
     */
    return this.getInvoiceById(
      organizationId,
      invoiceId
    );
  }

  
  /*
   * ---------------------------------------------------------
   * RESEND INVOICE EMAIL THROUGH SENDGRID
   *
   * Uses the existing Stripe invoice.
   * Does not create or modify Stripe invoices.
   * ---------------------------------------------------------
   */

  async resendInvoiceEmail(
    organizationId,
    invoiceId
  ) {
    requireOrganizationId(
      organizationId
    );

    requireInvoiceId(
      invoiceId
    );

    /*
     * Verify the current user's session.
     */
    const {
      data: { session },
      error: sessionError
    } = await supabase.auth.getSession();

    if (sessionError) {
      console.error(
        '[Invoices] Failed to get session:',
        sessionError
      );

      throw new Error(
        'Unable to verify your session.'
      );
    }

    if (!session?.access_token) {
      throw new Error(
        'You must be signed in to resend an invoice.'
      );
    }

    /*
     * Invoke the resend-only Edge Function.
     *
     * The backend:
     * - Verifies user and organization access
     * - Retrieves the existing Stripe invoice
     * - Confirms that the invoice is still open
     * - Sends the reminder through SendGrid
     *
     * No new Stripe invoice is created.
     */
    const {
      data,
      error
    } = await supabase.functions.invoke(
      'stripe-resend-invoice-email',
      {
        body: {
          organizationId,
          invoiceId
        },

        headers: {
          Authorization:
            `Bearer ${session.access_token}`
        }
      }
    );

    /*
     * Extract useful Edge Function errors.
     */
    if (error) {
      console.error(
        '[Invoices] Resend invoice email failed:',
        error
      );

      let message =
        error.message ||
        'Unable to resend invoice email.';

      try {
        if (
          error.context &&
          typeof error.context.json === 'function'
        ) {
          const errorBody =
            await error.context.json();

          if (errorBody?.message) {
            message = errorBody.message;
          }
        }
      } catch (parseError) {
        console.warn(
          '[Invoices] Unable to parse resend error:',
          parseError
        );
      }

      throw new Error(message);
    }

    if (!data || data.success !== true) {
      throw new Error(
        data?.message ||
        'Unable to resend invoice email.'
      );
    }

    console.log(
      '[Invoices] Invoice reminder accepted by SendGrid:',
      {
        organizationId,
        invoiceId,
        stripeInvoiceId: data.stripeInvoiceId,
        customerEmail: data.customerEmail
      }
    );

    /*
     * Return the Edge Function result.
     *
     * No invoice refresh is necessary because
     * resending does not change the invoice.
     */
    return {
      success: true,
      message:
        data.message ||
        'Invoice reminder email submitted successfully.',
      emailSent: data.emailSent === true,
      invoiceId,
      stripeInvoiceId: data.stripeInvoiceId,
      customerEmail: data.customerEmail,
      hostedInvoiceUrl: data.hostedInvoiceUrl
    };
  }



  /*
   * ---------------------------------------------------------
   * DELETE DRAFT INVOICE
   * ---------------------------------------------------------
   */

  async deleteInvoice(
    organizationId,
    invoiceId
  ) {
    requireOrganizationId(
      organizationId
    );

    requireInvoiceId(
      invoiceId
    );

    const invoice =
      await this.getInvoiceById(
        organizationId,
        invoiceId
      );

    if (
      invoice.status !== 'draft'
    ) {
      throw new Error(
        'Only draft invoices can be deleted.'
      );
    }

    /*
     * Delete items explicitly.
     * This does not depend on the FK having CASCADE.
     */
    const {
      error: itemError
    } =
      await supabase
        .schema('accounting')
        .from('invoice_items')
        .delete()
        .eq(
          'invoice_id',
          invoiceId
        )
        .eq(
          'organization_id',
          organizationId
        );

    if (itemError) {
      throw new Error(
        itemError.message ||
          'Unable to delete invoice items.'
      );
    }

    const {
      error: invoiceError
    } =
      await supabase
        .schema('accounting')
        .from('invoices')
        .delete()
        .eq(
          'id',
          invoiceId
        )
        .eq(
          'organization_id',
          organizationId
        );

    if (invoiceError) {
      throw new Error(
        invoiceError.message ||
          'Unable to delete invoice.'
      );
    }

    return true;
  }
}

export const invoiceService =
  new InvoiceService();